import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface Breadcrumb {
  label: string;
  href: string;
}

interface PageHeroProps {
  title: string;
  description?: string;
  breadcrumbs: Breadcrumb[];
  variant?: 'light' | 'image' | 'navy';
  image?: string;
  quote?: string;
}

export default function PageHero({ 
  title, 
  description, 
  breadcrumbs, 
  variant = 'light',
  image,
  quote
}: PageHeroProps) {
  if (variant === 'image') {
    return (
      <section className="relative h-[450px] overflow-hidden">
        <img src={image || "/images/campus/students-01.jpg"} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-12">
            <div className="bg-navy/90 text-white p-8 md:p-12 max-w-xl border-l-[8px] border-gold backdrop-blur-sm shadow-2xl animate-in fade-in slide-in-from-left-8 duration-700">
              <h1 className="text-4xl md:text-5xl font-playfair font-black mb-4 leading-tight">{title}</h1>
              {quote && (
                <p className="text-lg md:text-xl text-white/90 font-light mb-8 leading-relaxed italic">
                  "{quote}"
                </p>
              )}
              {description && !quote && <p className="text-xl text-white/90 font-light mb-8 leading-relaxed">{description}</p>}
              <Link to="/admissions" className="inline-flex items-center gap-2 text-gold font-extrabold text-lg hover:translate-x-1 transition-transform uppercase tracking-wider group">
                Commencer votre candidature <ChevronRight className="w-6 h-6 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`${variant === 'navy' ? 'bg-navy text-white' : 'bg-gray-50 text-navy'} py-12 md:py-16 border-b border-gray-200 overflow-hidden`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <nav className="flex items-center gap-2 text-sm opacity-60 mb-6 font-medium">
          <Link to="/" className="hover:underline">Home</Link>
          {breadcrumbs.map((crumb) => (
            <div key={crumb.href} className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 opacity-50" />
              <Link to={crumb.href} className="hover:underline">{crumb.label}</Link>
            </div>
          ))}
        </nav>
        
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6 italic leading-none animate-in fade-in slide-in-from-left-4 duration-500">
          {title}
        </h1>
        
        {description && (
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl leading-relaxed font-light opacity-90 animate-in fade-in slide-in-from-left-4 duration-700 delay-100">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}
